// US Business Day Utilities — single source of truth for holidays and work-week across all views.
//
// Call setOrgScheduling(profile.orgs.settings.scheduling) after profile loads to apply
// org-specific work week (Mon–Fri vs Mon–Sat) and holiday settings everywhere automatically.

let _orgScheduling = null

export function setOrgScheduling(settings) {
  _orgScheduling = settings ?? null
}

// Named holiday keys — used by SchedulingSettings to allow per-org removal/restore.
export const HOLIDAY_KEYS = {
  new_years:        { label: "New Year's Day",        description: 'January 1' },
  memorial_day:     { label: 'Memorial Day',           description: 'Last Monday of May' },
  independence_day: { label: 'Independence Day',       description: 'July 4 (observed)' },
  labor_day:        { label: 'Labor Day',              description: 'First Monday of September' },
  thanksgiving:     { label: 'Thanksgiving',           description: '4th Thursday of November' },
  thanksgiving_fri: { label: 'Day After Thanksgiving', description: 'Friday after Thanksgiving' },
  christmas_eve:    { label: 'Christmas Eve',          description: 'December 24' },
  christmas:        { label: 'Christmas Day',          description: 'December 25' },
}

function computeHoliday(key, year) {
  switch (key) {
    case 'new_years': return `${year}-01-01`
    case 'independence_day': {
      const dow = new Date(year, 6, 4).getDay()
      if (dow === 6) return `${year}-07-03`   // Saturday → Friday observed
      if (dow === 0) return `${year}-07-05`   // Sunday  → Monday observed
      return `${year}-07-04`
    }
    case 'memorial_day': {
      const d = new Date(year, 4, 31)
      while (d.getDay() !== 1) d.setDate(d.getDate() - 1)
      return d.toISOString().slice(0, 10)
    }
    case 'labor_day': {
      const d = new Date(year, 8, 1)
      while (d.getDay() !== 1) d.setDate(d.getDate() + 1)
      return d.toISOString().slice(0, 10)
    }
    case 'thanksgiving': {
      const d = new Date(year, 10, 1)
      while (d.getDay() !== 4) d.setDate(d.getDate() + 1)
      d.setDate(d.getDate() + 21)
      return d.toISOString().slice(0, 10)
    }
    case 'thanksgiving_fri': {
      const d = new Date(year, 10, 1)
      while (d.getDay() !== 4) d.setDate(d.getDate() + 1)
      d.setDate(d.getDate() + 22)
      return d.toISOString().slice(0, 10)
    }
    case 'christmas_eve': return `${year}-12-24`
    case 'christmas':     return `${year}-12-25`
    default: return null
  }
}

// Returns the calendar date for a named standard holiday in a given year (for display in settings UI).
export function getHolidayDate(key, year) {
  return computeHoliday(key, year)
}

function buildHolidaySet(year, settings) {
  const removed = settings?.removedHolidays ?? []
  const custom  = settings?.customHolidays  ?? []
  const h = []
  for (const key of Object.keys(HOLIDAY_KEYS)) {
    if (!removed.includes(key)) {
      const date = computeHoliday(key, year)
      if (date) h.push(date)
    }
  }
  for (const { date } of custom) {
    if (date?.slice(0, 4) === String(year)) h.push(date)
  }
  return new Set(h)
}

// Returns org-aware holidays for a year using current _orgScheduling (for calendar rendering).
export function getOrgHolidays(year) {
  return buildHolidaySet(year, _orgScheduling)
}

// Backward-compatible: returns standard US holidays ignoring org customization.
// Still used by components that haven't been updated, or for non-org contexts.
export function getUSHolidays(year) {
  return buildHolidaySet(year, null)
}

// Returns the array of work-day numbers (0=Sun … 6=Sat) from org settings.
// Handles migration from old workWeek string format.
function getWorkDays(settings) {
  if (Array.isArray(settings?.workDays)) return settings.workDays
  if (settings?.workWeek === 'mon-sat') return [1, 2, 3, 4, 5, 6]
  return [1, 2, 3, 4, 5] // default Mon–Fri
}

export function isOffDay(date) {
  const d = typeof date === 'string' ? new Date(date + 'T00:00:00') : date
  const dow = d.getDay()
  if (!getWorkDays(_orgScheduling).includes(dow)) return true
  const iso = d.toISOString().slice(0, 10)
  return buildHolidaySet(d.getFullYear(), _orgScheduling).has(iso)
}

// Add n business days to a date (n may be negative). n=0 returns startIso unchanged.
export function addBusinessDays(startIso, n) {
  if (n === 0) return startIso
  const d = new Date(startIso + 'T00:00:00')
  const step = n > 0 ? 1 : -1
  let remaining = Math.abs(n)
  while (remaining > 0) {
    d.setDate(d.getDate() + step)
    if (!isOffDay(d)) remaining--
  }
  return d.toISOString().slice(0, 10)
}

// Return the date itself if it's a business day, otherwise advance to the next business day.
export function nextBusinessDay(isoDate) {
  const d = new Date(isoDate + 'T00:00:00')
  while (isOffDay(d)) d.setDate(d.getDate() + 1)
  return d.toISOString().slice(0, 10)
}

// Count signed business-day delta from fromIso to toIso (positive = forward, negative = back).
export function businessDayDelta(fromIso, toIso) {
  if (fromIso === toIso) return 0
  return toIso > fromIso
    ? businessDaysBetween(fromIso, toIso)
    : -businessDaysBetween(toIso, fromIso)
}

// Count business days strictly after startIso up to and including endIso.
// Mon→Wed = 2.  Mon→Mon = 0.  Fri→Mon = 1 (weekend skipped).
export function businessDaysBetween(startIso, endIso) {
  const start = new Date(startIso + 'T00:00:00')
  const end   = new Date(endIso   + 'T00:00:00')
  if (end <= start) return 0
  let count = 0
  const d = new Date(start)
  d.setDate(d.getDate() + 1)
  while (d <= end) {
    if (!isOffDay(d)) count++
    d.setDate(d.getDate() + 1)
  }
  return count
}
